import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import {
  CardContainer,
  TwoColumnLayout,
  SectionTitle,
  FormContent,
  StepContainer,
} from '../components/PrebookingSharedComponents';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
import AttachmentPreview from '../components/AttachmentPreview';
import TruncatedFileName from '../components/TruncatedFileName';
import GlobeIcon from '../images/GlobeIcon';
import i18n from '../i18n';
import { getDurationTitle } from '../utils/timeUtils';
import { getActiveContractsFromUser, getContractById } from '../utils/contractUtils';

// Sargamicons SVG Icon Components (from Figma design)
const VideoIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.06667 1H2.6C1.71634 1 1 1.71634 1 2.6V7.4C1 8.28366 1.71634 9 2.6 9H8.06667C8.95032 9 9.66667 8.28366 9.66667 7.4V2.6C9.66667 1.71634 8.95032 1 8.06667 1Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M15.6667 1L11.6667 5L15.6667 9V1Z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const PhoneIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.7871 5.84615L8.12259 10.1539C8.58711 9.61539 11.529 6.46153 13 10.7692C13 10.7692 12.8452 13 9.67098 13C7.42582 13 5.10324 10.3077 3.47743 8.76922C2.08388 7.46153 1 5.76922 1 4.23076C1 1.07691 3.16773 1 3.16773 1C8.12257 2.69231 3.7871 5.84615 3.7871 5.84615Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinejoin="round"/>
  </svg>
);

const MonitorIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.1 16.5H11.9M3.42 1.5H15.58C16.6404 1.5 17.5 2.50736 17.5 3.75V11.4375C17.5 12.6801 16.6404 13.6875 15.58 13.6875H3.42C2.35961 13.6875 1.5 12.6801 1.5 11.4375V3.75C1.5 2.50736 2.35961 1.5 3.42 1.5Z" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 13C14 10.7909 12.1868 10 9.95 10H9.05C6.81325 10 5 10.7909 5 13M9.5 8.5C8.39543 8.5 7.5 7.60457 7.5 6.5V6C7.5 4.89543 8.39543 4 9.5 4C10.6046 4 11.5 4.89543 11.5 6V6.5C11.5 7.60457 10.6046 8.5 9.5 8.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BuildingIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 4V18.5999C19 19.9254 17.9255 20.9999 16.6 20.9999H5.4C4.07452 20.9999 3 19.9254 3 18.5999V4M14 21V17.8C14 17.3582 13.6418 17 13.2 17H8.8C8.35817 17 8 17.3582 8 17.8V21M11 7V9M11 12V14M15 7V9M15 12V14M7 7V9M7 12V14M1.8 1H20.2C20.6418 1 21 1.35817 21 1.8V3.2C21 3.64183 20.6418 4 20.2 4H1.8C1.35817 4 1 3.64183 1 3.2V1.8C1 1.35817 1.35817 1 1.8 1Z" stroke={color} strokeOpacity="0.8" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const NoteIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3C12 4.10457 11.1046 5 10 5C8.89543 5 8 4.10457 8 3M12 3C12 1.89543 11.1046 1 10 1C8.89543 1 8 1.89543 8 3M12 3H16.6C17.9255 3 19 4.07452 19 5.4V20.6C19 21.9255 17.9255 23 16.6 23H3.4C2.07452 23 1 21.9255 1 20.6V5.4C1 4.07452 2.07452 3 3.4 3H8M5 13H9M5 10H15M5 16H7M12.306 13.7761L11.9291 15.2836C11.9112 15.3552 11.8552 15.4112 11.7836 15.4291L10.2761 15.806C10.0741 15.8565 10.0741 16.1435 10.2761 16.194L11.7836 16.5709C11.8552 16.5888 11.9112 16.6448 11.9291 16.7164L12.306 18.2239C12.3565 18.4259 12.6435 18.4259 12.694 18.2239L13.0709 16.7164C13.0888 16.6448 13.1448 16.5888 13.2164 16.5709L14.7239 16.194C14.9259 16.1435 14.9259 15.8565 14.7239 15.806L13.2164 15.4291C13.1448 15.4112 13.0888 15.3552 13.0709 15.2836L12.694 13.7761C12.6435 13.5741 12.3565 13.5741 12.306 13.7761Z" stroke={color} strokeOpacity="0.8" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 1.66667H14.1667V0H12.5V1.66667H4.16667V0H2.5V1.66667H1.66667C0.75 1.66667 0 2.41667 0 3.33333V16.6667C0 17.5833 0.75 18.3333 1.66667 18.3333H15C15.9167 18.3333 16.6667 17.5833 16.6667 16.6667V3.33333C16.6667 2.41667 15.9167 1.66667 15 1.66667ZM15 16.6667H1.66667V5.83333H15V16.6667Z" fill={color}/>
  </svg>
);

const TimeIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.4925 0C3.3525 0 0 3.36 0 7.5C0 11.64 3.3525 15 7.4925 15C11.64 15 15 11.64 15 7.5C15 3.36 11.64 0 7.4925 0ZM7.5 13.5C4.185 13.5 1.5 10.815 1.5 7.5C1.5 4.185 4.185 1.5 7.5 1.5C10.815 1.5 13.5 4.185 13.5 7.5C13.5 10.815 10.815 13.5 7.5 13.5Z" fill={color}/>
    <path d="M7.875 3.75H6.75V8.25L10.6875 10.5938L11.25 9.66563L7.875 7.78125V3.75Z" fill={color}/>
  </svg>
);

const UserIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.3125 8.25C11.3511 8.25 14.625 11.5239 14.625 15.5625C14.625 15.8732 14.3732 16.125 14.0625 16.125H0.5625C0.25184 16.125 0 15.8732 0 15.5625C0 11.5239 3.27392 8.25 7.3125 8.25ZM7.3125 9.375C4.08491 9.375 1.43637 11.8464 1.1521 15H13.4729C13.1886 11.8464 10.5401 9.375 7.3125 9.375ZM9.75 3.5625C9.75 2.21631 8.65869 1.125 7.3125 1.125C5.96631 1.125 4.875 2.21631 4.875 3.5625C4.875 4.90869 5.96631 6 7.3125 6C8.65869 6 9.75 4.90869 9.75 3.5625ZM10.875 3.5625C10.875 5.53001 9.28001 7.125 7.3125 7.125C5.34499 7.125 3.75 5.53001 3.75 3.5625C3.75 1.59499 5.34499 0 7.3125 0C9.28001 0 10.875 1.59499 10.875 3.5625Z" fill={color}/>
  </svg>
);

const LocationIcon = ({ color = '#45446b', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 0C3.58 0 0 3.58 0 8C0 13.54 7.06 19.45 7.35 19.71C7.54 19.89 7.77 20 8 20C8.23 20 8.46 19.89 8.65 19.71C8.94 19.45 16 13.54 16 8C16 3.58 12.42 0 8 0ZM8 17.71C5.59 15.36 2 11.04 2 8C2 4.69 4.69 2 8 2C11.31 2 14 4.69 14 8C14 11.04 10.41 15.36 8 17.71ZM8 4C5.79 4 4 5.79 4 8C4 10.21 5.79 12 8 12C10.21 12 12 10.21 12 8C12 5.79 10.21 4 8 4ZM8 10C6.9 10 6 9.1 6 8C6 6.9 6.9 6 8 6C9.1 6 10 6.9 10 8C10 9.1 9.1 10 8 10Z" fill={color}/>
  </svg>
);

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(69, 68, 107, 0.1);
  }
`;

const SummaryLabel = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(69, 68, 107, 0.8);
  line-height: 1.2;
  letter-spacing: 0.15px;
  min-width: 200px;
  flex-shrink: 0;
`;

const SummaryValue = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #45446b;
  line-height: 1.4;
  letter-spacing: 0.5px;
  text-align: right;
  flex: 1;
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid rgba(69, 68, 107, 0.4);
  border-radius: 50px;
  background: white;

  span {
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #45446b;
    letter-spacing: 0.15px;
    line-height: 1.2;
  }
`;

const MessageContent = styled.div`
  padding: 16px;
  background: #f6f8fb;
  border-radius: 4px;
  margin: 12px 0;

  p {
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #45446b;
    line-height: 1.4;
    letter-spacing: 0.5px;
    white-space: pre-wrap;
  }
`;

const PresenceIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;

  svg {
    flex-shrink: 0;
  }

  span {
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #45446b;
    letter-spacing: 0.5px;
  }
`;

const DetailsRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InputField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2px;
`;

const InputLabel = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(69, 68, 107, 0.8);
  line-height: 1.2;
  letter-spacing: 0.15px;
  padding-left: 8px;
`;

const InputValue = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #45446b;
  line-height: 1.4;
  letter-spacing: 0.5px;
  padding: 2px 8px;
`;

const TwoColumnRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const CustomerInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: #fff8e1;
  border: 1px solid #fcc02e;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const WarningText = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #45446b;
  line-height: 1.4;
`;

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fcc02e" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: '1px' }}>
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const PrebookingSummaryStep = ({ formData, prebookingId, wizardMode, originalInterpretationDate }) => {
  const { user } = useContext(AppContext);
  const { colors } = useContext(ThemeContext);
  const availableContracts = React.useMemo(() => getActiveContractsFromUser(user), [user]);
  const selectedContract = React.useMemo(() => {
    const selected = getContractById(availableContracts, formData?.selectedContractId);
    return selected?.raw || availableContracts[0]?.raw || null;
  }, [availableContracts, formData?.selectedContractId]);

  // Access languages and skills from user object
  const languages = formData?.selectableLanguages || [];

  // Flatten skills object into array for easier lookup
  const skills = React.useMemo(() => {
    if (!formData?.selectableSkills) return [];
    // selectableSkills is an object like { languageId: [skill1, skill2, ...] }
    // Flatten all skill arrays into a single array
    return Object.values(formData.selectableSkills).flat();
  }, [formData?.selectableSkills]);

  const isHeroTolk = isHeroBrandEnabled();

  // Get contract allowed levels for display
  const levels = React.useMemo(() => {
    const allLevels = selectedContract?.allowedLevels || [];
    return allLevels.map(l => ({
      id: l.levelId,
      name: l.levelName,
      interpreterTier: l.interpreterTier,
    }));
  }, [selectedContract?.allowedLevels]);

  // Hashmaps for lookups
  const presenceLabelMap = {
    remote: () => i18n('remote'),
    onsite: () => i18n('on_site'),
    notice: () => i18n('announcement')
  };

  // Returns SVG icon components based on mode
  const getModeIcon = (mode) => {
    switch (mode) {
      case 'data': return <VideoIcon size={18} />;
      case 'call': return <PhoneIcon size={18} />;
      case 'conference': return <MonitorIcon size={18} />;
      default: return <VideoIcon size={18} />;
    }
  };

  // Returns SVG icon components based on presence
  const getPresenceIconComponent = () => {
    switch (formData?.presence) {
      case 'remote': return getModeIcon(formData.mode);
      case 'onsite': return <BuildingIcon size={18} />;
      case 'notice': return <NoteIcon size={18} />;
      default: return <VideoIcon size={18} />;
    }
  };

  const modeLabelMap = {
    data: () => i18n('video_call_interpretation'),
    call: () => i18n('phone_interpretation'),
    conference: () => i18n('conference_interpretation')
  };

  const genderLabelMap = {
    female: () => i18n('female_interpreter'),
    male: () => i18n('male_interpreter'),
    does_not_matter: () => i18n('does_not_matter'),
    null: () => i18n('does_not_matter'),
    undefined: () => i18n('does_not_matter')
  };

  const getPresenceLabel = () => {
    if (!formData?.presence) return '-';
    const lookup = presenceLabelMap[formData.presence];
    return lookup ? lookup() : formData.presence;
  };

  const getModeLabel = (mode) => {
    if (!mode) return '-';
    const lookup = modeLabelMap[mode];
    return lookup ? lookup() : mode;
  };

  const getLanguageName = (languageId) => {
    if (!languageId && languageId !== 0) return '-';
    // IDs are UUIDs (strings), normalize both to string for comparison
    const language = languages?.find(lang => String(lang.id) === String(languageId));
    return language?.name || `Language ${languageId}`;
  };

  const getTargetLanguageName = (targetLanguageId) => {
    if (!targetLanguageId) return null;
    const entry = formData.targetLanguages?.find(l => String(l.id) === String(targetLanguageId));
    return entry?.name || formData.targetLanguageName || null;
  };

  const getSkillName = (skillId) => {
    if (!skillId && skillId !== 0) return '-';
    // IDs are UUIDs (strings), normalize both to string for comparison
    const skill = skills?.find(s => String(s.id) === String(skillId));
    return skill?.name || `Skill ${skillId}`;
  };

  const getLevelName = (levelId) => {
    if (!levelId && levelId !== 0) return '-';

    // Handle special 'other_interpreter' case
    if (levelId === 'other_interpreter') {
      return i18n('other_interpreter_level', 'Other interpreter');
    }

    // IDs are UUIDs (strings), normalize both to string for comparison
    const level = levels?.find(l => String(l.id) === String(levelId));

    if (!level) {
      return `${i18n('interpreter_level_label')} ${levelId}`;
    }

    // If level has a name (like "Highest"), use it
    if (level.name) {
      return level.name;
    }

    // For hardcoded levels with levelLabel (A, B, C, D, E), format with localized prefix
    if (level.levelLabel && level.levelLabel !== 'highest') {
      return `${i18n('interpreter_level_label')} ${level.levelLabel}`;
    }

    return level.label || `${i18n('interpreter_level_label')} ${levelId}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return moment(date).format('DD.M.YYYY');
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return moment(time).format('HH:mm');
  };


  const getCostAllocationLabel = () => {
    // Simply read from the saved costAllocationGroup object
    if (!formData.costAllocationGroup) {
      return '-';
    }

    const group = formData.costAllocationGroup;
    return (group.publicName || group.name) + (group.invoiceRecipientRef ? ` (${group.invoiceRecipientRef})` : '');
  };

  const getGenderLabel = (gender) => {
    if (!gender) return i18n('does_not_matter');

    // Check if it's a UUID (skill ID) - UUIDs contain dashes and are longer than 20 chars
    if (typeof gender === 'string' && gender.includes('-') && gender.length > 20) {
      return getSkillName(gender);
    }

    // Fallback to old hardcoded values for backward compatibility
    const key = gender === null ? 'null' : gender === undefined ? 'undefined' : gender;
    const lookup = genderLabelMap[key];
    return lookup ? lookup() : (gender || '-');
  };

  // Guard: Ensure formData exists
  if (!formData) {
    return (
      <StepContainer colors={colors}>
        <CardContainer>
          <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(69, 68, 107, 0.8)' }}>
            {i18n('error') || 'Error'}: {i18n('missing_data') || 'Missing form data'}
          </div>
        </CardContainer>
      </StepContainer>
    );
  }

  const showEditWarning = wizardMode === 'edit';
  const showCostWarning = showEditWarning && originalInterpretationDate &&
    moment(originalInterpretationDate).diff(moment(), 'hours') < 24;

  return (
    <StepContainer colors={colors}>
      {/* Edit mode warning */}
      {showEditWarning && (
        <WarningBanner>
          <WarningIcon />
          <WarningText>{i18n('we_will_cancel_this_booking_and_make_a_new_one')}</WarningText>
        </WarningBanner>
      )}
      {showCostWarning && (
        <WarningBanner>
          <WarningIcon />
          <WarningText>{i18n('additional_cost_will_be_charged')}</WarningText>
        </WarningBanner>
      )}

      {/* Details Section */}
      <CardContainer>
        <TwoColumnLayout>
          <SectionTitle colors={colors}>{i18n('details')}</SectionTitle>
          <FormContent>
            <SummaryContent>
              {/* First Row: Mode/Presence and Language */}
              <DetailsRow>
                <PresenceIndicator>
                  {getPresenceIconComponent()}
                  <span>
                    {formData.mode && formData.presence === 'remote'
                      ? getModeLabel(formData.mode)
                      : getPresenceLabel()}
                  </span>
                </PresenceIndicator>

                <PresenceIndicator>
                  <GlobeIcon size={18} />
                  <span>
                    {getLanguageName(formData.selectedLanguageId)}
                    {formData.selectedDialect !== undefined && formData.selectedDialect !== null && ` (${getSkillName(formData.selectedDialect)})`}
                  </span>
                </PresenceIndicator>
              </DetailsRow>

              {/* Target Language - Show when user has multiple target languages and one is selected */}
              {getTargetLanguageName(formData.selectedTargetLanguageId) && (
                <DetailsRow>
                  <PresenceIndicator>
                    <GlobeIcon size={18} />
                    <span>{i18n('targetLanguage')}: {getTargetLanguageName(formData.selectedTargetLanguageId)}</span>
                  </PresenceIndicator>
                </DetailsRow>
              )}

              {/* Time Slots - Only for remote and onsite */}
              {formData.presence !== 'notice' && (
                <>
                  {formData.timeSlots && formData.timeSlots.length > 0 ? (
                    formData.timeSlots.map((slot, index) => (
                      <DetailsRow key={index}>
                        <PresenceIndicator>
                          <CalendarIcon size={18} />
                          <span>{formatDate(slot?.date)}</span>
                        </PresenceIndicator>

                        <PresenceIndicator>
                          <TimeIcon size={18} />
                          <span>
                            {formatTime(slot?.startTime)} - {formatTime(slot?.endTime)}
                            {slot?.duration > 0 && ` (${getDurationTitle(slot.duration)})`}
                          </span>
                        </PresenceIndicator>
                      </DetailsRow>
                    ))
                  ) : (
                    <span style={{ color: 'rgba(69, 68, 107, 0.6)' }}>No time slots selected</span>
                  )}
                </>
              )}

              {/* Delivery Date - Only for announcements */}
              {formData.presence === 'notice' && formData.lastDeliveryDate && (
                <DetailsRow>
                  <PresenceIndicator>
                    <CalendarIcon size={18} />
                    <span>{formatDate(formData.lastDeliveryDate)}</span>
                  </PresenceIndicator>
                </DetailsRow>
              )}

              {/* Gender Preference Row - Not shown for announcements */}
              {formData.gender && formData.gender !== 'does_not_matter' && formData.presence !== 'notice' && (
                <DetailsRow>
                  <PresenceIndicator>
                    <UserIcon size={18} />
                    <span>{getGenderLabel(formData.gender)}</span>
                  </PresenceIndicator>
                </DetailsRow>
              )}

              {/* Competence Levels and Experience Skill */}
              {((formData.competenceLevel && formData.competenceLevel.length > 0) ||
                (formData.experienceSkill && formData.experienceSkill !== 'does_not_matter_experience')) && (
                <ChipContainer>
                  {/* Competence Levels */}
                  {formData.competenceLevel && formData.competenceLevel
                    .filter(id => id !== null && id !== undefined)
                    .map(levelId => (
                      <Chip key={levelId}>
                        <span>{getLevelName(levelId)}</span>
                      </Chip>
                    ))}
                  {/* Experience Skill */}
                  {formData.experienceSkill && formData.experienceSkill !== 'does_not_matter_experience' && (
                    <Chip key={formData.experienceSkill}>
                      <span>{getSkillName(formData.experienceSkill)}</span>
                    </Chip>
                  )}
                </ChipContainer>
              )}

              {/* Unqualified Interpreter - Only show when selected or in comments */}
              {(formData.competenceLevel?.includes('other_interpreter') ||
                formData.additionalInfoForHandler?.includes('REQUESTED LEVEL INCLUDES: Unqualified interpreter')) && (
                <SummaryRow>
                  <SummaryLabel>{i18n('other_interpreter_level', 'Unqualified interpreter')}</SummaryLabel>
                  <SummaryValue>{i18n('yes')}</SummaryValue>
                </SummaryRow>
              )}
            </SummaryContent>

            {/* Location for On-site */}
            {formData.presence === 'onsite' && (
              <SummaryContent style={{ marginTop: '16px', borderTop: '1px solid rgba(69, 68, 107, 0.1)', paddingTop: '16px' }}>
                <DetailsRow>
                  <PresenceIndicator>
                    <LocationIcon size={18} />
                    <span>
                      {formData.streetAddress && `${formData.streetAddress}, `}
                      {formData.postCode && `${formData.postCode} `}
                      {formData.city || '-'}
                    </span>
                  </PresenceIndicator>
                </DetailsRow>
                {formData.arrivalInstruction && (
                  <SummaryRow>
                    <SummaryLabel>{i18n('arrival_instruction_at_location')}</SummaryLabel>
                    <SummaryValue>{formData.arrivalInstruction}</SummaryValue>
                  </SummaryRow>
                )}
                {formData.acceptBeyondKmLimit !== undefined && formData.contractDistanceLimit > 0 && (
                  <SummaryRow>
                    <SummaryLabel>{i18n('preferences_accept_beyond_km').replace('{}', formData.contractDistanceLimit)}</SummaryLabel>
                    <SummaryValue>{formData.acceptBeyondKmLimit ? i18n('yes') : i18n('no')}</SummaryValue>
                  </SummaryRow>
                )}
                {formData.acceptBeyondKmLimit && formData.kmBeyondLimit && formData.contractDistanceLimit > 0 && (
                  <SummaryRow>
                    <SummaryLabel>{i18n('preferences_km_beyond_limit').replace('{}', formData.contractDistanceLimit)}</SummaryLabel>
                    <SummaryValue>
                      {formData.kmBeyondLimit === 'unlimited'
                        ? i18n('unlimited')
                        : `${formData.kmBeyondLimit} ${i18n('km_unit')}`}
                    </SummaryValue>
                  </SummaryRow>
                )}
                {formData.onsiteFallbackHandling && (
                  <SummaryRow>
                    <SummaryLabel>{i18n('preferences_onsite_fallback')}</SummaryLabel>
                    <SummaryValue>
                      {formData.onsiteFallbackHandling === 'assign_remote_interpreter'
                        ? i18n('assign_remote_interpreter')
                        : i18n('cancel_booking')}
                    </SummaryValue>
                  </SummaryRow>
                )}
                {formData.onsiteFallbackHandling === 'assign_remote_interpreter' && formData.backupCallTypeForOnSite && (
                  <SummaryRow>
                    <SummaryLabel>{i18n('preferences_remote_backup_connection_type')}</SummaryLabel>
                    <SummaryValue>
                      {formData.backupCallTypeForOnSite === 'call'
                        ? i18n('preferences_remote_backup_phone')
                        : formData.backupCallTypeForOnSite === 'conference'
                          ? i18n('preferences_remote_backup_conference', 'Conference call')
                          : i18n('preferences_remote_backup_video')}
                    </SummaryValue>
                  </SummaryRow>
                )}
              </SummaryContent>
            )}

            {/* Message for Announcement */}
            {formData.presence === 'notice' && (
              <SummaryContent style={{ marginTop: '16px', borderTop: '1px solid rgba(69, 68, 107, 0.1)', paddingTop: '16px' }}>
                {formData.messageContent && (
                  <>
                    <SummaryLabel style={{ marginBottom: '8px' }}>{i18n('message_content')}</SummaryLabel>
                    <MessageContent>
                      <p>{formData.messageContent}</p>
                    </MessageContent>
                  </>
                )}
              </SummaryContent>
            )}
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>

      {isHeroTolk ? (
        /* Staging: Merged Customer Information + Invoicing */
        <CardContainer>
          <TwoColumnLayout>
            <SectionTitle colors={colors}>{i18n('customer_information')}</SectionTitle>
            <FormContent>
              <CustomerInfoContent>
                {/* Order made by and Ordering unit - side by side */}
                <TwoColumnRow>
                  <InputField>
                    <InputLabel>{i18n('order_made_by')}</InputLabel>
                    <InputValue>{user?.name || user?.username || '-'}</InputValue>
                  </InputField>
                  <InputField>
                    <InputLabel>{i18n('ordering_unit')}</InputLabel>
                    <InputValue>{user?.userGroup ? (user.userGroup.publicName || user.userGroup.name) : '-'}</InputValue>
                  </InputField>
                </TwoColumnRow>

                <InputField>
                  <InputLabel>{i18n('contract_label', 'Contract')}</InputLabel>
                  <InputValue>{formData.selectedContractName || '-'}</InputValue>
                </InputField>

                {/* Invoicing fields merged into Customer Information */}
                <InputField>
                  <InputLabel>{i18n('cost_allocation')}</InputLabel>
                  <InputValue>{getCostAllocationLabel()}</InputValue>
                </InputField>

                <InputField>
                  <InputLabel>{user?.userGroup?.invoiceRefTitle || i18n('additional_invoicing_information')}</InputLabel>
                  <InputValue>{formData.additionalInvoicingInfo || '-'}</InputValue>
                </InputField>

                {/* VAT selection - only shown when vatPolicy is 'select' */}
                {user?.userGroup?.vatPolicy === 'select' && (
                  <InputField>
                    <InputLabel>{i18n('prebooking_vat_question')}</InputLabel>
                    <InputValue>
                      {formData.vatIncluded !== false
                        ? i18n('prebooking_vat_applied')
                        : i18n('prebooking_vat_exempt')}
                    </InputValue>
                  </InputField>
                )}

                {/* External order ID - moved to customer info per TUL-5433 */}
                {user?.userGroup?.externalOrderIdUsed && (
                  <InputField>
                    <InputLabel>{user?.userGroup?.externalOrderIdTitle || i18n('external_order_id')}</InputLabel>
                    <InputValue>{formData.externalOrderId || '-'}</InputValue>
                  </InputField>
                )}

                <InputField>
                  <InputLabel>{i18n('additional_information_for_order_handler')}</InputLabel>
                  <InputValue>{formData.additionalInfoForHandler || '-'}</InputValue>
                </InputField>
              </CustomerInfoContent>
            </FormContent>
          </TwoColumnLayout>
        </CardContainer>
      ) : (
        /* Non-staging: Separate Customer Information and Invoicing sections */
        <>
          <CardContainer>
            <TwoColumnLayout>
              <SectionTitle colors={colors}>{i18n('customer_information')}</SectionTitle>
              <FormContent>
                <CustomerInfoContent>
                  {/* Order made by and Ordering unit - side by side */}
                  <TwoColumnRow>
                    <InputField>
                      <InputLabel>{i18n('order_made_by')}</InputLabel>
                      <InputValue>{user?.name || user?.username || '-'}</InputValue>
                    </InputField>
                    <InputField>
                      <InputLabel>{i18n('ordering_unit')}</InputLabel>
                      <InputValue>{user?.userGroup ? (user.userGroup.publicName || user.userGroup.name) : '-'}</InputValue>
                    </InputField>
                  </TwoColumnRow>

                  <InputField>
                    <InputLabel>{i18n('contract_label', 'Contract')}</InputLabel>
                    <InputValue>{formData.selectedContractName || '-'}</InputValue>
                  </InputField>
                </CustomerInfoContent>
              </FormContent>
            </TwoColumnLayout>
          </CardContainer>

          <CardContainer>
            <TwoColumnLayout>
              <SectionTitle colors={colors}>{i18n('invoicing')}</SectionTitle>
              <FormContent>
                <CustomerInfoContent>
                  <InputField>
                    <InputLabel>{i18n('cost_allocation')}</InputLabel>
                    <InputValue>{getCostAllocationLabel()}</InputValue>
                  </InputField>

                  <InputField>
                    <InputLabel>{user?.userGroup?.invoiceRefTitle || i18n('additional_invoicing_information')}</InputLabel>
                    <InputValue>{formData.additionalInvoicingInfo || '-'}</InputValue>
                  </InputField>

                  {/* VAT selection - only shown when vatPolicy is 'select' */}
                  {user?.userGroup?.vatPolicy === 'select' && (
                    <InputField>
                      <InputLabel>{i18n('prebooking_vat_question')}</InputLabel>
                      <InputValue>
                        {formData.vatIncluded !== false
                          ? i18n('prebooking_vat_applied')
                          : i18n('prebooking_vat_exempt')}
                      </InputValue>
                    </InputField>
                  )}

                  {/* External order ID - moved to customer info per TUL-5433 */}
                  {user?.userGroup?.externalOrderIdUsed && (
                    <InputField>
                      <InputLabel>{user?.userGroup?.externalOrderIdTitle || i18n('external_order_id')}</InputLabel>
                      <InputValue>{formData.externalOrderId || '-'}</InputValue>
                    </InputField>
                  )}

                  {/* Customer info for announcement */}
                  {formData.presence === 'notice' && (
                    <>
                      <InputField>
                        <InputLabel>{i18n('customer_name_for_invoice')}</InputLabel>
                        <InputValue>{formData.customerName || '-'}</InputValue>
                      </InputField>

                      <InputField>
                        <InputLabel>{i18n('date_of_birth_for_invoice')}</InputLabel>
                        <InputValue>{formatDate(formData.dateOfBirth)}</InputValue>
                      </InputField>
                    </>
                  )}

                  <InputField>
                    <InputLabel>{i18n('additional_information_for_order_handler')}</InputLabel>
                    <InputValue>{formData.additionalInfoForHandler || '-'}</InputValue>
                  </InputField>
                </CustomerInfoContent>
              </FormContent>
            </TwoColumnLayout>
          </CardContainer>
        </>
      )}

      {/* Assigned Interpreter Section - Only shown for confirmed prebookings */}
      {(formData.interpreterName || formData.interpreterEmail || formData.interpreterPhone) && (
        <CardContainer>
          <TwoColumnLayout>
            <SectionTitle colors={colors}>{i18n('interpreter')}</SectionTitle>
            <FormContent>
              <CustomerInfoContent>
                {formData.interpreterName && (
                  <InputField>
                    <InputLabel>{i18n('name')}</InputLabel>
                    <InputValue>{formData.interpreterName}</InputValue>
                  </InputField>
                )}
                {formData.interpreterPhone && (
                  <InputField>
                    <InputLabel>{i18n('phone_number_title')}</InputLabel>
                    <InputValue>{formData.interpreterPhone}</InputValue>
                  </InputField>
                )}
                {formData.interpreterEmail && (
                  <InputField>
                    <InputLabel>{i18n('email')}</InputLabel>
                    <InputValue>{formData.interpreterEmail}</InputValue>
                  </InputField>
                )}
                {formData.interpreterLevelNames?.length > 0 && (
                  <InputField>
                    <InputLabel>{i18n('interpreter_level_label')}</InputLabel>
                    <InputValue>{formData.interpreterLevelNames.join(', ')}</InputValue>
                  </InputField>
                )}
              </CustomerInfoContent>
            </FormContent>
          </TwoColumnLayout>
        </CardContainer>
      )}

      {/* Information for Interpreter Section */}
      <CardContainer>
        <TwoColumnLayout>
          <SectionTitle colors={colors}>{i18n('information_for_interpreter')}</SectionTitle>
          <FormContent>
            <CustomerInfoContent>
              {/* Patient name - Hidden for Hero brand (staging) and announcements */}
              {!isHeroTolk && formData.presence !== 'notice' && (
                <InputField>
                  <InputLabel>{i18n('patient_name')}</InputLabel>
                  <InputValue>{formData.clientName || '-'}</InputValue>
                </InputField>
              )}

              {/* Subject - Not shown for announcements */}
              {formData.presence !== 'notice' && (
                <InputField>
                  <InputLabel>{i18n('subject')}</InputLabel>
                  <InputValue>{formData.subject || '-'}</InputValue>
                </InputField>
              )}

              {/* Announcement-specific fields */}
              {formData.presence === 'notice' && (
                <>
                  {formData.alternativeContactPerson && (
                    <InputField>
                      <InputLabel>{i18n('to_whom_message_forwarded_if_not_customer')}</InputLabel>
                      <InputValue>{formData.alternativeContactPerson}</InputValue>
                    </InputField>
                  )}
                  {formData.mainPhoneNumber && (
                    <InputField>
                      <InputLabel>{i18n('main_telephone_number_interpreter_will_call')}</InputLabel>
                      <InputValue>{formData.mainPhoneNumber}</InputValue>
                    </InputField>
                  )}
                  {formData.additionalPhoneNumbers && formData.additionalPhoneNumbers.length > 0 && (
                    <InputField>
                      <InputLabel>{i18n('additional_telephone_numbers_interpreter_will_call')}</InputLabel>
                      <InputValue>{formData.additionalPhoneNumbers.filter(p => p?.trim()).join(', ')}</InputValue>
                    </InputField>
                  )}
                </>
              )}

              {/* URL for conference mode */}
              {formData.mode === 'conference' && formData.videoMeetingUrl && (
                <InputField>
                  <InputLabel>{i18n('url_for_video_meeting')}</InputLabel>
                  <InputValue>{formData.videoMeetingUrl}</InputValue>
                </InputField>
              )}

              {/* Reference document / Attachment */}
              {prebookingId && !formData.attachment ? (
                <InputField>
                  <InputLabel>{i18n('customer_documents') || 'Customer Documents'}</InputLabel>
                  <AttachmentPreview
                    prebookingId={prebookingId}
                    isCustomer={true}
                  />
                </InputField>
              ) : formData.attachment ? (
                <InputField>
                  <InputLabel>{i18n('reference_document')}</InputLabel>
                  <InputValue style={{ display: 'flex', minWidth: 0 }}>
                    <TruncatedFileName
                      filename={formData.attachment.filename || '-'}
                      fileSize={formData.attachment.size}
                    />
                  </InputValue>
                </InputField>
              ) : null}

              <InputField>
                <InputLabel>{i18n('recipients_of_confirmation_and_change_notifications')}</InputLabel>
                <InputValue>{[user?.email, ...(formData.ccEmails || [])].filter(Boolean).join(', ') || '-'}</InputValue>
              </InputField>

              {/* Phone numbers for announcement - Currently not in form data model */}
            </CustomerInfoContent>
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>
    </StepContainer>
  );
};

PrebookingSummaryStep.propTypes = {
  formData: PropTypes.object.isRequired,
  prebookingId: PropTypes.string,
  wizardMode: PropTypes.string,
  originalInterpretationDate: PropTypes.number,
};

export default PrebookingSummaryStep;